<template>
  <div ref="imageContainer" class="base-image">
    <div class="base-image__placeholder" :class="{ 'is-hidden': !showPlaceholder }">
      <a-image
        v-if="showPlaceholderBlur"
        :src="placeholderBlurSrc"
        class="base-image__placeholder-blur"
        :alt="alt"
        width="100%"
        height="100%"
        :show-loader="false"
        :preview="false"
        loading="eager"
        decoding="sync"
        fetchpriority="high"
      />
      <div class="base-image__loading-dots">
        <div class="dot"></div>
        <div class="dot"></div>
        <div class="dot"></div>
      </div>
      <div class="base-image__loading-text">加载中...</div>
    </div>

    <div
      class="base-image__optimized"
      :class="{ 'is-previewable': preview && !error }"
      @click="handlePreview"
    >
      <a-image
        :src="activeImageSrc"
        :alt="alt"
        width="100%"
        height="100%"
        :show-loader="false"
        :srcset="shouldLoad ? srcset : ''"
        :sizes="shouldLoad ? sizes : ''"
        :preview="false"
        class="base-image__inner"
        :class="{ 'is-loading': showPlaceholder }"
        :decoding="imageDecodingMode"
        :fetchpriority="imageFetchPriority"
        @load="handleLoad"
        @error="handleError"
      />
    </div>

    <client-only>
      <a-image-preview
        v-if="preview"
        v-model:visible="previewVisible"
        :src="previewSrc"
        :mask-closable="previewProps?.maskClosable ?? true"
        render-to-body
        v-bind="previewProps"
      />
    </client-only>

    <div v-if="error" class="base-image__error">
      <slot name="error">
        <span>图片加载失败</span>
      </slot>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onUnmounted, ref, watch } from 'vue'
import {
  getOssFormatUrl,
  getOssPlaceholderUrl,
  getOssProgressiveUrl,
  getOssUrlWithoutAvifForPreview,
  isOssUrl,
} from '~/utils'

/**
 * @description Base image component with lazy loading, OSS progressive placeholder, and stable source switching.
 * @param {string} src - Image source path or OSS URL.
 * @param {string} alt - Image alt text.
 * @param {string} srcset - Responsive image candidates.
 * @param {string} sizes - Responsive image sizes.
 * @param {boolean} preload - Whether to eagerly load the image.
 * @param {boolean} preview - Whether preview is enabled.
 * @param {Object|undefined} previewProps - Extra preview props forwarded to Arco preview.
 * @param {boolean} progressive - Whether to enable OSS progressive processing.
 * @param {boolean} disableOssFormat - Whether to keep OSS original format.
 * @param {boolean} isBanner - Whether the image is a banner image.
 * @param {boolean} disableOssOptimization - Whether to skip OSS formatting and progressive processing.
 */
const props = defineProps({
  src: { type: String, required: true },
  alt: { type: String, default: '' },
  srcset: { type: String, default: '' },
  sizes: { type: String, default: '' },
  preload: { type: Boolean, default: false },
  preview: { type: Boolean, default: false },
  previewProps: { type: Object, default: undefined },
  progressive: { type: Boolean, default: true },
  disableOssFormat: { type: Boolean, default: false },
  isBanner: { type: Boolean, default: false },
  disableOssOptimization: { type: Boolean, default: false },
})

const emit = defineEmits(['error'])

const { withBase } = useBasePath()

const EMPTY_IMAGE = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7'
const VIEWPORT_PRELOAD_OFFSET = 160

const currentSrc = ref(props.src)
const activeImageSrc = ref(EMPTY_IMAGE)
const loading = ref(true)
const error = ref(false)
const imageContainer = ref(null)
const previewVisible = ref(false)
const hasLoadedImage = ref(false)

let formatRequestId = 0
let observer = null
let viewportProbeTimer = null
let viewportProbeFrameId = null
let loadStateProbeTimer = null
let loadStateProbeFrameId = null
let layoutShiftInterval = null
let touchCleanups = []

const shouldLoadImmediately = computed(() => props.preload || props.preview)
const shouldLoad = ref(shouldLoadImmediately.value)
const resolvedUrl = computed(() => withBase(currentSrc.value))
const previewSrc = computed(() => getOssUrlWithoutAvifForPreview(resolvedUrl.value))
const showPlaceholder = computed(() => loading.value && !error.value && !hasLoadedImage.value)
const useOssProgressive = computed(() => {
  return props.progressive && !props.disableOssOptimization && isOssUrl(resolvedUrl.value)
})
const imageDecodingMode = computed(() => (shouldLoadImmediately.value ? 'sync' : 'async'))
const imageFetchPriority = computed(() => {
  if (shouldLoadImmediately.value) return 'high'
  if (shouldLoad.value) return 'auto'
  return 'low'
})
const placeholderBlurSrc = computed(() => {
  if (!useOssProgressive.value) return ''
  return getOssPlaceholderUrl(resolvedUrl.value) || ''
})
const showPlaceholderBlur = computed(() => Boolean(placeholderBlurSrc.value) && !props.preview)

const getInnerImageElement = () => {
  return imageContainer.value?.querySelector(
    '.base-image__optimized .arco-image-img, .base-image__optimized img'
  )
}

const clearViewportProbe = () => {
  if (!import.meta.client) return
  if (viewportProbeFrameId !== null) {
    cancelAnimationFrame(viewportProbeFrameId)
    viewportProbeFrameId = null
  }
  if (viewportProbeTimer !== null) {
    clearTimeout(viewportProbeTimer)
    viewportProbeTimer = null
  }
}

const clearLoadStateProbe = () => {
  if (!import.meta.client) return
  if (loadStateProbeFrameId !== null) {
    cancelAnimationFrame(loadStateProbeFrameId)
    loadStateProbeFrameId = null
  }
  if (loadStateProbeTimer !== null) {
    clearTimeout(loadStateProbeTimer)
    loadStateProbeTimer = null
  }
}

const isNearViewport = () => {
  if (!import.meta.client || !imageContainer.value) return false

  const rect = imageContainer.value.getBoundingClientRect()
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight || 0
  const viewportWidth = window.innerWidth || document.documentElement.clientWidth || 0

  return (
    rect.bottom >= -VIEWPORT_PRELOAD_OFFSET &&
    rect.right >= 0 &&
    rect.top <= viewportHeight + VIEWPORT_PRELOAD_OFFSET &&
    rect.left <= viewportWidth
  )
}

const ensureNearViewportLoads = () => {
  if (shouldLoad.value || !import.meta.client) return
  if (isNearViewport()) {
    shouldLoad.value = true
  }
}

const handleLoad = () => {
  if (!loading.value || !shouldLoad.value || error.value) return
  loading.value = false
  hasLoadedImage.value = true
}

const handleError = () => {
  if (error.value) return
  loading.value = false
  error.value = true
  emit('error')
}

const syncLoadStateFromDom = () => {
  if (!import.meta.client || !shouldLoad.value || error.value) return
  const img = getInnerImageElement()
  if (!img || !img.complete) return

  if (img.naturalWidth > 0) {
    handleLoad()
    return
  }

  if (img.currentSrc || img.src) {
    handleError()
  }
}

const queueLoadStateProbe = () => {
  if (!import.meta.client) return
  clearLoadStateProbe()
  nextTick(() => {
    syncLoadStateFromDom()
    loadStateProbeFrameId = requestAnimationFrame(() => {
      syncLoadStateFromDom()
      loadStateProbeFrameId = null
    })
    loadStateProbeTimer = window.setTimeout(() => {
      syncLoadStateFromDom()
      loadStateProbeTimer = null
    }, 180)
  })
}

const resolveDisplaySrc = async (sourceUrl, requestId) => {
  const nextResolvedUrl = withBase(sourceUrl)
  const isOss = isOssUrl(nextResolvedUrl)
  const shouldProgressive = props.progressive && !props.disableOssOptimization && isOss

  if (import.meta.client && isOss && !props.disableOssOptimization) {
    try {
      let urlToFormat = nextResolvedUrl

      if (shouldProgressive) {
        urlToFormat = getOssProgressiveUrl(urlToFormat)
      }

      if (requestId !== formatRequestId) return ''

      if (props.disableOssFormat) {
        return urlToFormat
      }

      return await getOssFormatUrl(urlToFormat, undefined, {
        isBanner: props.isBanner,
      })
    } catch (err) {
      if (requestId !== formatRequestId) return ''
      console.warn('Failed to format OSS image URL:', err)
      return shouldProgressive ? getOssProgressiveUrl(nextResolvedUrl) : nextResolvedUrl
    }
  }

  return shouldProgressive ? getOssProgressiveUrl(nextResolvedUrl) : nextResolvedUrl
}

const preloadResolvedImage = (sourceUrl) => {
  if (!import.meta.client || !hasLoadedImage.value) {
    return Promise.resolve()
  }

  return new Promise((resolve, reject) => {
    const probeImage = new window.Image()
    let settled = false

    const cleanup = () => {
      probeImage.onload = null
      probeImage.onerror = null
    }

    const finish = (callback) => {
      if (settled) return
      settled = true
      cleanup()
      callback()
    }

    probeImage.onload = () => finish(resolve)
    probeImage.onerror = () =>
      finish(() => reject(new Error(`Failed to preload image: ${sourceUrl}`)))
    probeImage.src = sourceUrl

    if (probeImage.complete) {
      if (probeImage.naturalWidth > 0) {
        finish(resolve)
      } else {
        finish(() => reject(new Error(`Failed to preload image: ${sourceUrl}`)))
      }
    }
  })
}

const loadActiveImage = async () => {
  if (!shouldLoad.value) {
    activeImageSrc.value = EMPTY_IMAGE
    return
  }

  const requestId = ++formatRequestId
  const nextDisplaySrc = await resolveDisplaySrc(currentSrc.value, requestId)

  if (!nextDisplaySrc || requestId !== formatRequestId) return

  if (activeImageSrc.value === nextDisplaySrc) {
    queueLoadStateProbe()
    return
  }

  try {
    await preloadResolvedImage(nextDisplaySrc)
  } catch (err) {
    if (requestId !== formatRequestId) return
    console.warn('Failed to preload next image source:', err)
  }

  if (requestId !== formatRequestId) return

  activeImageSrc.value = nextDisplaySrc
  queueLoadStateProbe()
}

const handlePreview = () => {
  if (props.preview && !error.value && !loading.value) {
    previewVisible.value = true
  }
}

watch(previewVisible, (visible) => {
  if (!import.meta.client) return

  if (visible) {
    nextTick(() => {
      const previewImgs = document.querySelectorAll('.arco-image-preview-img')

      previewImgs.forEach((img) => {
        img.style.touchAction = 'none'

        if (!('ontouchstart' in window)) return

        let isPinching = false
        let singleFingerActive = false
        let lastPinchDist = 0

        const getTouchDist = (t1, t2) =>
          Math.hypot(t2.clientX - t1.clientX, t2.clientY - t1.clientY)

        const dispatchWheel = (deltaY) => {
          const wrapper = document.querySelector('.arco-image-preview-wrapper')
          if (!wrapper) return

          const wheelEvent = new WheelEvent('wheel', {
            bubbles: true,
            cancelable: true,
            deltaY,
            deltaX: 0,
            view: window,
          })

          wrapper.dispatchEvent(wheelEvent)
        }

        const touchToMouse = (touch, type, buttons = 1) => {
          const mouseEvent = new MouseEvent(type, {
            bubbles: true,
            cancelable: true,
            view: window,
            clientX: touch.clientX,
            clientY: touch.clientY,
            screenX: touch.screenX,
            screenY: touch.screenY,
            button: 0,
            buttons,
          })

          img.dispatchEvent(mouseEvent)
        }

        const onTouchStart = (event) => {
          if (event.touches.length === 2) {
            isPinching = true
            singleFingerActive = false
            lastPinchDist = getTouchDist(event.touches[0], event.touches[1])
            event.preventDefault()
            return
          }

          if (event.touches.length === 1 && !isPinching) {
            singleFingerActive = true
            event.preventDefault()
            touchToMouse(event.touches[0], 'mousedown', 1)
          }
        }

        const onTouchMove = (event) => {
          if (event.touches.length === 2) {
            singleFingerActive = false
            event.preventDefault()

            const distance = getTouchDist(event.touches[0], event.touches[1])
            const delta = distance - lastPinchDist
            const step = 25

            if (Math.abs(delta) >= step) {
              const count = Math.floor(Math.abs(delta) / step)
              const deltaY = delta > 0 ? -step : step

              for (let i = 0; i < count; i++) {
                dispatchWheel(deltaY)
              }

              lastPinchDist = distance
            }
            return
          }

          if (event.touches.length === 1 && !isPinching) {
            event.preventDefault()
            touchToMouse(event.touches[0], 'mousemove', 1)
          }
        }

        const onTouchEnd = (event) => {
          if (event.touches.length < 2) {
            isPinching = false
          }

          if (event.changedTouches.length === 0) return

          if (event.touches.length === 0 && singleFingerActive) {
            touchToMouse(event.changedTouches[0], 'mouseup', 0)
            singleFingerActive = false
          }

          event.preventDefault()
        }

        img.addEventListener('touchstart', onTouchStart, { passive: false })
        img.addEventListener('touchmove', onTouchMove, { passive: false })
        img.addEventListener('touchend', onTouchEnd, { passive: false })

        touchCleanups.push(() => {
          img.removeEventListener('touchstart', onTouchStart)
          img.removeEventListener('touchmove', onTouchMove)
          img.removeEventListener('touchend', onTouchEnd)
          img.style.touchAction = ''
        })
      })
    })
  } else {
    touchCleanups.forEach((fn) => fn())
    touchCleanups = []
  }
})

onMounted(() => {
  if (shouldLoadImmediately.value) {
    shouldLoad.value = true
    void loadActiveImage()
    return
  }

  if (!import.meta.client || !imageContainer.value) return

  ensureNearViewportLoads()

  // @reason 旧版 WebView/浏览器可能缺少 IntersectionObserver；此时直接加载图片，
  // 避免首屏图片组件在挂载阶段抛错，连带导致页面主体白屏。
  if (typeof IntersectionObserver === 'undefined') {
    shouldLoad.value = true
    void loadActiveImage()
    return
  }

  observer = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) {
          shouldLoad.value = true
          observer?.unobserve(entry.target)
        }
      }
    },
    {
      root: null,
      rootMargin: '100px',
      threshold: 0.01,
    }
  )

  if (!shouldLoad.value) {
    observer.observe(imageContainer.value)
  }

  viewportProbeFrameId = requestAnimationFrame(() => {
    ensureNearViewportLoads()
    viewportProbeFrameId = null
  })
  viewportProbeTimer = window.setTimeout(() => {
    ensureNearViewportLoads()
    viewportProbeTimer = null
  }, 250)

  if (!shouldLoad.value) {
    layoutShiftInterval = setInterval(() => {
      if (shouldLoad.value) {
        clearInterval(layoutShiftInterval)
        layoutShiftInterval = null
      } else {
        if (isNearViewport()) {
          shouldLoad.value = true
        }
      }
    }, 500)
  }
})

onUnmounted(() => {
  if (layoutShiftInterval) {
    clearInterval(layoutShiftInterval)
    layoutShiftInterval = null
  }
  if (observer && imageContainer.value) {
    observer.unobserve(imageContainer.value)
    observer.disconnect()
    observer = null
  }

  clearViewportProbe()
  clearLoadStateProbe()
  touchCleanups.forEach((fn) => fn())
  touchCleanups = []
})

watch(
  () => props.src,
  (newSrc) => {
    currentSrc.value = newSrc
    loading.value = true
    error.value = false

    if (shouldLoad.value) {
      void loadActiveImage()
    }
  }
)

watch(shouldLoadImmediately, (value) => {
  if (value) {
    shouldLoad.value = true
  }
})

watch(shouldLoad, (value) => {
  if (!value) return
  if (layoutShiftInterval) {
    clearInterval(layoutShiftInterval)
    layoutShiftInterval = null
  }

  if (observer && imageContainer.value) {
    observer.unobserve(imageContainer.value)
  }

  void loadActiveImage()
})
</script>

<style lang="scss" scoped>
:deep(.base-image__optimized),
:deep(.base-image__inner),
:deep(.arco-image),
:deep(.arco-image-img),
:deep(img) {
  border-radius: 0;
}

.base-image {
  position: relative;
  overflow: hidden;
  width: 100%;
  height: 100%;
  contain: layout;

  &__placeholder {
    position: absolute;
    inset: 0;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 8px;
    background: rgba($bg-white, 0.95);
    z-index: 10;
    pointer-events: none;
    opacity: 1;
    visibility: visible;
    transition:
      opacity 0.24s ease,
      visibility 0.24s ease;

    &.is-hidden {
      opacity: 0;
      visibility: hidden;
    }
  }

  &__placeholder-blur {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    object-position: center;
    filter: blur(3px);
    z-index: 0;

    :deep(.arco-image) {
      width: 100%;
      height: 100%;
    }

    :deep(img) {
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__loading-dots {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 6px;

    .dot {
      width: 6px;
      height: 6px;
      border-radius: 50%;
      background-color: rgba($text-subtitle-color, 0.75);
      animation: pulse 1.2s ease-in-out infinite;

      &:nth-child(2) {
        animation-delay: 0.12s;
      }

      &:nth-child(3) {
        animation-delay: 0.24s;
      }
    }
  }

  &__loading-text {
    position: relative;
    z-index: 1;
    font-size: 12px;
    line-height: 1;
    color: $text-subtitle-color;
  }

  &__optimized {
    width: 100%;
    height: 100%;
    opacity: 1;
    transition: opacity 0.3s ease;

    &.is-previewable {
      cursor: zoom-in;
    }
  }

  &__inner {
    :deep(img) {
      width: 100%;
      height: 100%;
      object-fit: cover;
      display: block;
      will-change: opacity;
      backface-visibility: hidden;
      transition: opacity 0.32s ease;
    }

    :deep(.arco-image) {
      width: 100%;
      height: 100%;
    }

    :deep(.arco-image-with-footer-inner) {
      width: 100%;
      height: 100%;
    }

    :deep(.arco-image-error) {
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      gap: 4px;
      background-color: $bg-color;
      border-radius: inherit;
    }

    :deep(.arco-image-error-icon) {
      width: 40%;
      height: 40%;
      max-width: 32px;
      max-height: 32px;
      min-width: 14px;
      min-height: 14px;
      font-size: clamp(14px, 40%, 32px);
      color: $text-subtitle-color;
    }

    :deep(.arco-image-error-alt) {
      display: none;
    }

    &.is-loading {
      :deep(img) {
        opacity: 0;
      }
    }
  }

  &__skeleton {
    position: absolute;
    inset: 0;
    background: linear-gradient(
      90deg,
      $bg-light-gray 25%,
      $border-dashed-color 50%,
      $bg-light-gray 75%,
      $border-dashed-color 100%
    );
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;

    &::before {
      content: '';
      position: absolute;
      inset: 0;
      background: rgba($bg-white, 0.8);
    }
  }

  &__error {
    background-color: $bg-color;
    color: $text-content-color;
    padding: 1rem;
    text-align: center;
    border-radius: 8px;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.4;
    transform: scale(0.8);
  }
  50% {
    opacity: 1;
    transform: scale(1);
  }
}

@keyframes shimmer {
  0% {
    background-position: -200% 0;
  }
  100% {
    background-position: 200% 0;
  }
}
</style>
